<template>
  <div class="rectangle-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="矩形属性" name="properties">
        <div class="group-block">
          <n-text depth="3" class="group-label">矩形类型</n-text>
          <n-radio-group
            :value="options.mode"
            size="small"
            :disabled="!canEdit"
            @update:value="handleModeChange"
          >
            <n-space vertical size="small">
              <n-radio v-for="item in rectangleModes" :key="item.value" :value="item.value">
                {{ item.label }}
              </n-radio>
            </n-space>
          </n-radio-group>
        </div>
        <div class="group-block">
          <n-space vertical size="small">
            <n-checkbox
              :checked="hasConstructionLines"
              :disabled="!canEdit || isConstructionLinesToggleLocked"
              @update:checked="handleToggleConstructionLines"
            >
              添加构造线
            </n-checkbox>
          </n-space>
          <n-radio-group
            v-if="hasConstructionLines"
            :value="constructionLineMode"
            size="small"
            class="construction-group"
            :disabled="!canEdit"
            @update:value="handleConstructionLineMode"
          >
            <n-space vertical size="small">
              <n-radio :value="RectangleConstructionLines.Corners">从角点</n-radio>
              <n-radio :value="RectangleConstructionLines.Midpoints">从中点</n-radio>
            </n-space>
          </n-radio-group>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div v-for="(label, index) in cornerLabels" :key="label" class="param-row">
            <n-text depth="3" class="param-label">{{ label }}</n-text>
            <div class="param-inputs">
              <div class="param-field">
                <n-text depth="3" class="param-axis">X</n-text>
                <n-input-number
                  :value="corners2d[index]?.[0] ?? null"
                  size="small"
                  :disabled="!canEditParameters"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleCornerInput(index, 'x', value)"
                />
              </div>
              <div class="param-field">
                <n-text depth="3" class="param-axis">Y</n-text>
                <n-input-number
                  :value="corners2d[index]?.[1] ?? null"
                  size="small"
                  :disabled="!canEditParameters"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleCornerInput(index, 'y', value)"
                />
              </div>
            </div>
          </div>
          <div v-if="showCenter" class="param-row">
            <n-text depth="3" class="param-label">中心</n-text>
            <div class="param-inputs">
              <div class="param-field">
                <n-text depth="3" class="param-axis">X</n-text>
                <n-input-number
                  :value="center2d?.[0] ?? null"
                  size="small"
                  :disabled="!canEditParameters"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleCenterInput('x', value)"
                />
              </div>
              <div class="param-field">
                <n-text depth="3" class="param-axis">Y</n-text>
                <n-input-number
                  :value="center2d?.[1] ?? null"
                  size="small"
                  :disabled="!canEditParameters"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleCenterInput('y', value)"
                />
              </div>
            </div>
          </div>
        </div>
      </n-collapse-item>

    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, ref} from 'vue';
import {NCheckbox, NCollapse, NCollapseItem, NInputNumber, NRadio, NRadioGroup, NSpace, NText,} from 'naive-ui';
import {type PropertyManagerContext,} from '@/core';
import {useGetSelectRectangle, useViewer} from '@/hooks';
import {
  convertMmToM,
  convertMToMm,
  createDefaultRectangleOptions,
  isRectangleConstructionLinesToggleLocked,
  normalizeRectangleDrawingOptions,
  RectangleConstructionLines,
  type RectangleOptions,
  type RectangleParameters,
  roundToEightDecimalPlaces,
  type SketchCollectionObject,
  SketchRectangleCommand,
  SketchRectanglePropertyService,
} from '@puppycad/cad-core';

defineOptions({
  inheritAttrs: false,
});

interface RectanglePropertySectionProps {
  /** 属性面板上下文，用于把矩形属性编辑统一收口到 cad-core 草图历史。 */
  context?: PropertyManagerContext;
}

const props = defineProps<RectanglePropertySectionProps>();

/** 通过共享 Hook 获取渲染器实例，直接访问场景数据 */
const { getViewer } = useViewer();
const {
  getSelected,
  onChanged: onRectangleChanged,
  onRectangleParametersChanged,
  onDrawingPreviewChanged,
} = useGetSelectRectangle();
/** cad-core 矩形属性服务，集中处理集合参数读写。 */
const rectanglePropertyService = new SketchRectanglePropertyService();



/** 默认矩形绘制选项，必须复用 cad-core 默认值以保证面板和绘制器一致。 */
const defaultOptions: RectangleOptions = createDefaultRectangleOptions();


/**
 * 本地响应式矩形参数。
 * 说明：从绘制处理器初始化，绘制参数变化时本地同步更新。
 */
const localRectangleParameters = ref<RectangleParameters | null>(null);

/**
 * 本地响应式矩形绘制选项。
 * 说明：从渲染器初始化，updateOptions 执行后本地同步更新。
 */
const localRectangleOptions = ref<RectangleOptions>({ ...defaultOptions });
localRectangleOptions.value = getViewer()?.getRectangleDrawingOptions?.() ?? { ...defaultOptions };
localRectangleParameters.value = getViewer()?.getRectangleDrawingParameters?.() ?? null;
/** 当前是否有矩形集合处于选中态。 */
const isRectangleSelected = ref(false);
/** 当前参数是否来自绘制预览，而不是已选矩形实体。 */
const isDrawingPreview = ref(false);

/**
 * 将选中矩形的参数同步到本地状态。
 *
 * @param rectangle 当前选中的矩形集合；为 null 时清空参数。
 */
function syncRectangleParameters(rectangle: SketchCollectionObject | null): void {
  localRectangleParameters.value = rectanglePropertyService.snapshotParameters(rectangle);
}

/**
 * 将选中矩形的类型选项同步到本地状态。
 *
 * @param rectangle 当前选中的矩形集合；为 null 时回到绘制选项。
 */
function syncRectangleOptions(rectangle: SketchCollectionObject | null): void {
  if (rectangle) {
    localRectangleOptions.value = rectanglePropertyService.snapshotOptions(rectangle, localRectangleOptions.value);
    return;
  }
  localRectangleOptions.value = getViewer()?.getRectangleDrawingOptions?.() ?? { ...defaultOptions };
}
/** 订阅矩形选中变化，选中已有矩形时从集合对象回读参数。 */
const rectangleChangedSub = onRectangleChanged().subscribe((rectangle) => {
  isRectangleSelected.value = rectangle !== null;
  syncRectangleOptions(rectangle);
  if (!isDrawingPreview.value) {
    syncRectangleParameters(rectangle);
  }
});
/** 订阅矩形绘制处理器的实时参数流，绘制预览时同步更新面板数值。 */
const rectangleParamsSub = onRectangleParametersChanged().subscribe((params) => {
  if (params) {
    localRectangleParameters.value = params;
    return;
  }
  localRectangleParameters.value = rectanglePropertyService.snapshotParameters(getSelected());
});
/** 订阅绘制预览状态，避免预览参数覆盖已选矩形参数。 */
const drawingPreviewSub = onDrawingPreviewChanged().subscribe((drawing) => {
  isDrawingPreview.value = drawing;
});


onBeforeUnmount(() => {
  rectangleChangedSub.unsubscribe();
  rectangleParamsSub.unsubscribe();
  drawingPreviewSub.unsubscribe();
});
/** 有渲染器实例即可编辑选项 */
const canEdit = computed(() => Boolean(getViewer()));
/** 选中矩形实体且非绘制预览时允许编辑参数。 */
const canEditParameters = computed(() => Boolean(isRectangleSelected.value && !isDrawingPreview.value && getViewer()));
const options = computed(() => localRectangleOptions.value);

/** 中心类矩形会锁定“添加构造线”，但仍允许用户切换构造线来源。 */
const isConstructionLinesToggleLocked = computed(() => (
  isRectangleConstructionLinesToggleLocked(options.value.mode)
));
/** 当前是否应显示构造线来源设置。 */
const hasConstructionLines = computed(() => (
  isConstructionLinesToggleLocked.value
  || options.value.constructionLines !== RectangleConstructionLines.None
));
/** 当前构造线来源，中心类矩形在旧状态缺失时回退到该模式默认来源。 */
const constructionLineMode = computed(() => (
  normalizeRectangleDrawingOptions(options.value).constructionLines
));


const corners2d = computed(() => {
  const list = localRectangleParameters.value?.corners2d ?? [];
  return list.map((c) => {
    if (!c) return [0, 0];
    return [
      roundToEightDecimalPlaces(convertMToMm(c[0])),
      roundToEightDecimalPlaces(convertMToMm(c[1])),
    ];
  });
});
const center2d = computed(() => {
  const c = localRectangleParameters.value?.center2d ?? null;
  if (!c) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(c[0])),
    roundToEightDecimalPlaces(convertMToMm(c[1])),
  ];
});

const showCenter = computed(() => (
  options.value.mode === SketchRectangleCommand.Center
  || options.value.mode === SketchRectangleCommand.ThreePointCenter
));

const rectangleModes: Array<{ label: string; value: SketchRectangleCommand }> = [
  { label: '角点矩形', value: SketchRectangleCommand.Corner },
  { label: '中心矩形', value: SketchRectangleCommand.Center },
  { label: '三点角矩形', value: SketchRectangleCommand.ThreePointCorner },
  { label: '三点中心矩形', value: SketchRectangleCommand.ThreePointCenter },
  { label: '平行四边形', value: SketchRectangleCommand.Parallelogram },
];

const cornerLabels = ['角点1', '角点2', '角点3', '角点4'];
const defaultExpanded = ['properties', 'parameters'];

/**
 * 更新矩形绘制选项。
 *
 * @param patch 矩形绘制选项增量。
 */
function updateOptions(patch: Partial<RectangleOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getRectangleDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }

  const nextOptions = normalizeRectangleDrawingOptions({ ...currentOptions, ...patch });

  // 先让 cad-core setter 做最终兜底，再从绘制器回读实际状态，保证面板与底层一致。
  renderer.setRectangleDrawingOptions?.(nextOptions);
  localRectangleOptions.value = renderer.getRectangleDrawingOptions?.() ?? nextOptions;

}

function handleModeChange(value: SketchRectangleCommand): void {
  updateOptions({ mode: value });
}

function handleToggleConstructionLines(value: boolean): void {
  const renderer = getViewer();
  const cachedSource = renderer?.getRectangleDrawingConstructionLineSource?.()
    ?? RectangleConstructionLines.Corners;

  updateOptions({
    // 重新勾选时恢复当前矩形类型缓存的来源，避免用户从中点取消后再次勾选被重置到从角点。
    constructionLines: value ? cachedSource : RectangleConstructionLines.None,
  });
}

function handleConstructionLineMode(value: RectangleConstructionLines): void {
  updateOptions({ constructionLines: value });
}

function handleCornerInput(index: number, axis: 'x' | 'y', value: number | null): void {
  if (value === null || !Number.isFinite(value)) {
    return;
  }
  updateSelectedRectangle((rectangle) => {

    // 将用户输入的毫米值转换为米级用于底层几何属性
    rectanglePropertyService.updateCornerCoordinate(rectangle, {
      cornerIndex: index,
      axis,
      value: convertMmToM(value),
    });

  });
}

function handleCenterInput(axis: 'x' | 'y', value: number | null): void {
  if (value === null || !Number.isFinite(value)) {
    return;
  }
  updateSelectedRectangle((rectangle) => {

    // 将用户输入的毫米值转换为米级用于底层几何属性
    rectanglePropertyService.updateCenterCoordinate(rectangle, {
      axis,
      value: convertMmToM(value),
    });

  });
}

/**
 * 在矩形属性 setter 执行前捕获历史 before 快照。
 *
 * @param rectangle 被编辑的矩形集合。
 */
function prepareRectanglePropertyEdit(rectangle: SketchCollectionObject): void {
  props.context?.prepareSketchPropertyEdit?.(rectangle);
}

/**
 * 完成矩形属性编辑并提交草图定义变化。
 *
 * @param rectangle 被编辑的矩形集合。
 */
function finalizeRectanglePropertyEdit(rectangle: SketchCollectionObject): void {
  props.context?.finalizeSketchPropertyEdit?.(rectangle, {
    reason: 'property-edit',
    sourceObjectId: rectangle.uuid,
  });
}

/**
 * 更新当前选中矩形，并在修改后从 cad-core 对象回读最终参数。
 *
 * @param mutator 实际几何修改函数。
 */
function updateSelectedRectangle(mutator: (rectangle: SketchCollectionObject) => void): void {
  const selectedRectangle = getSelected();
  if (!selectedRectangle || !getViewer()) {
    return;
  }

  prepareRectanglePropertyEdit(selectedRectangle);
  mutator(selectedRectangle);
  finalizeRectanglePropertyEdit(selectedRectangle);

  /**
   * 约束求解可能把用户输入拉回到合法几何状态，
   * 因此必须在 finalize 后从底层对象重新读取面板参数。
   */
  syncRectangleParameters(selectedRectangle);
  getViewer()?.interactionStateManager?.requestVisualRefresh?.();
}


/** 当前组件创建时已经选中的矩形集合。 */
const initialSelectedRectangle = getSelected();
isRectangleSelected.value = initialSelectedRectangle !== null;
syncRectangleOptions(initialSelectedRectangle);
syncRectangleParameters(initialSelectedRectangle);


</script>

<style scoped>
.rectangle-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.group-block + .group-block {
  margin-top: 12px;
}

.group-label {
  margin-bottom: 8px;
  display: block;
}

.construction-group {
  margin-top: 8px;
  padding-left: 6px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 8px;
  align-items: center;
}

.param-inputs {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.param-field {
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 6px;
  align-items: center;
}

.param-axis {
  font-size: 12px;
}

.param-label {
  font-size: 12px;
}
</style>
